import React, { useEffect, useRef, useState } from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import { Flex, theme, Typography } from 'antd';
import { ComponentValue } from '../../../../data/rsTypes';
import { t } from '../../../../util';
import { defaultHeight, defaultWidth } from '../../../Components/componentUtils';
import { ComponentTypes } from '../../../Components/types';
import { useAppStore } from '../../../store';
import { isComponentValid } from '../../util';
import {
	StyledInputWrapper,
	StyledMultiLineValue,
	StyledPlaceholder,
	StyledTextAreaWrapper,
	StyledTextComponentWrapper,
	StyledTextEditingTyping,
} from './TextComponent.styled';

const opacitySuffix = '1A';
const { useToken } = theme;
const { Text } = Typography;

//This constant is defined as 6 is minimum font size accepted by RS backend for font size and we set it to 10 for visibility in mobile view
const MINIMUM_FONT_SIZE = 10;

// This is minimum height per line which is minimum font-size 10 + line height of 1
const MINIMUM_HEIGHT_PER_LINE = 11;

// This determines a mathematial relationship between the vertical (font size)
// and horizontal (character width) dimensions of text. Since we use Courier
// in the final processed pdf, we use it's associated constant here so that
// the web app views look as close as possible to the final pdf
// Taken from https://pearsonified.com/2012/01/characters-per-line.php
export const CHARACTER_CONSTANT = 1.67;

const componentStyles: React.CSSProperties = {
	border: '1px solid',
};

const validComponentStyles: React.CSSProperties = {
	border: '1px solid',
};

const counterStyle = (scale, currentCharCount, maxChars): React.CSSProperties => ({
	position: 'absolute',
	bottom: `-${20 * scale}px`,
	left: 0,
	right: 0,
	width: '100%',
	justifyContent: 'flex-end',
	fontSize: `${11 * scale}px`,
	color: currentCharCount >= maxChars ? colorPalette.red6 : colorPalette.neutral8,
	pointerEvents: 'none',
});

interface Props {
	componentValue: ComponentValue;
	textAlign: string;
	multiline?: boolean;
	onChange: (value: string) => void;
	selected: boolean;
	pageBoundingBox: { height: number; width: number };
	defaultValue?: string;
	scale: number;
	componentWidth?: number;
	componentHeight?: number;
}

// Calculates the length of a string considering new lines and the component width
const stringLengthWithNewLine = (text: string, componentWidth: number): number => {
	if (!text) {
		return 0;
	}

	const charsPerLine = (componentWidth - 10) / (MINIMUM_FONT_SIZE / CHARACTER_CONSTANT);
	const lines = text.split('\n');
	let totalChars = 0;
	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		totalChars += line.length;

		// Only add extra characters for newlines (not for the last line)
		if (i < lines.length - 1) {
			const charsToAdd =
				line.length === 0
					? charsPerLine
					: Math.max(0, charsPerLine - (line.length % charsPerLine));

			if (line.length === 0 || line.length % charsPerLine !== 0) {
				totalChars += charsToAdd;
			}
		}
	}

	return Math.floor(totalChars);
};

const calculateCharacterLimit = (
	componentWidth: number,
	componentHeight: number
): number => {
	// Subtracting 10 from width as this is leftMargin for the text
	const widthToFit = (componentWidth - 10) * (componentHeight / MINIMUM_HEIGHT_PER_LINE);
	const fontSizeConstant = MINIMUM_FONT_SIZE / CHARACTER_CONSTANT;
	const numberOfCharacters = Math.floor(widthToFit / fontSizeConstant);

	return numberOfCharacters;
};

const findOptimalFontSize = (
	componentHeight: number,
	componentWidth: number,
	value: string | undefined,
	maxFontSize: number
): number => {
	// Handle undefined or null values
	const safeValue = value || '';
	const stringSize = stringLengthWithNewLine(safeValue, componentWidth * defaultWidth);
	// Subtracting 10 from width as this is leftMargin for the text
	const widthToFit = (componentWidth - 10) * (componentHeight / MINIMUM_HEIGHT_PER_LINE);

	let fontSize = maxFontSize;
	if (stringSize) {
		if (stringSize * (fontSize / CHARACTER_CONSTANT) > widthToFit) {
			const widthPerCharacter = widthToFit / stringSize;
			fontSize = widthPerCharacter * CHARACTER_CONSTANT;
			fontSize = fontSize > maxFontSize ? maxFontSize : fontSize;
			fontSize = fontSize < MINIMUM_FONT_SIZE ? MINIMUM_FONT_SIZE : fontSize;
		}
	}
	return fontSize;
};

export const TextComponent = (props: Props) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const defaultValue = props.defaultValue;
	const value = props.componentValue.value ?? defaultValue;
	const isValid = isComponentValid(value, ComponentTypes.TextComponent);
	const [setComponentValue] = useAppStore(state => [state.setComponentValue]);

	const { token } = useToken();

	const [maximumCharacterLimit, setMaximumCharacterLimit] = useState<number>(1000); // Default large value

	useEffect(() => {
		if (wrapperRef.current) {
			const rawLimit = calculateCharacterLimit(
				props.componentWidth * defaultWidth,
				props.componentHeight * defaultHeight
			);
			setMaximumCharacterLimit(rawLimit);
		}
	}, [props.componentWidth, props.componentHeight]);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (value && setComponentValue) {
			const optimalFontSize = findOptimalFontSize(
				props.componentHeight * defaultHeight,
				props.componentWidth * defaultWidth,
				props.componentValue.value || '',
				14
			);
			props.componentValue.font_size = optimalFontSize;
			setComponentValue({
				...props.componentValue,
			});
		}
	}, [value]);

	const borderColor = isValid ? colorPalette.green8 : colorPalette.lavender6;
	const backgroundColor = borderColor + opacitySuffix;
	const currentCharCount = stringLengthWithNewLine(
		props.componentValue.value ?? '',
		props.componentWidth * defaultWidth
	);

	let displayValue: React.ReactNode;
	if (value) {
		if (props.multiline) {
			displayValue = (
				<StyledMultiLineValue
					fontSize={props.componentValue.font_size * props.scale}
					style={{
						textAlign: props.textAlign as React.CSSProperties['textAlign'],
						marginLeft: 10 * props.scale,
					}}
				>
					{value}
				</StyledMultiLineValue>
			);
		} else {
			displayValue = (
				<Text
					id="readonly-text"
					style={{
						fontSize: `${14 * props.scale}px`,
						paddingLeft: `${10 * props.scale}px`,
						fontFamily: 'Courier, sans-serif',
					}}
				>
					{value}
				</Text>
			);
		}
	} else {
		displayValue = (
			<Flex
				align="start"
				gap={8}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: props.textAlign,
					width: '100%',
				}}
			>
				<StyledTextEditingTyping scale={props.scale} />
				<StyledPlaceholder scale={props.scale}>
					{t('esign-pilet-ui:signerPage.content.fields.enterText')}
				</StyledPlaceholder>
			</Flex>
		);
	}

	if (!props.selected) {
		return (
			<StyledTextComponentWrapper
				ref={wrapperRef}
				style={isValid ? validComponentStyles : componentStyles}
				textAlign={props.textAlign}
				borderColor={borderColor}
				backgroundColor={backgroundColor}
				multiline={props.multiline}
				token={token}
				id={`TextComponent-${props.componentValue.component_id}-unselected`}
				data-testid={`TextComponent-${props.componentValue.component_id}-unselected`}
			>
				{displayValue}
			</StyledTextComponentWrapper>
		);
	}

	const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const newValue = e.target.value;
		const effectiveCharCount = stringLengthWithNewLine(
			newValue,
			props.componentWidth * defaultWidth
		);

		if (effectiveCharCount <= maximumCharacterLimit) {
			props.onChange(newValue);
		} else {
			let truncatedValue = newValue;
			while (
				truncatedValue.length > 0 &&
				stringLengthWithNewLine(truncatedValue, props.componentWidth * defaultWidth) >
					maximumCharacterLimit
			) {
				truncatedValue = truncatedValue.slice(0, -1);
			}
			props.onChange(truncatedValue);
		}
	};

	return props.multiline ? (
		<div
			ref={wrapperRef}
			style={{
				position: 'relative',
				height: '100%',
				width: '100%',
			}}
		>
			<StyledTextAreaWrapper
				textAlign={props.textAlign}
				borderColor={borderColor}
				backgroundColor={backgroundColor}
				color={colorPalette.neutral13}
				token={token}
				autoFocus
				onChange={onChange}
				value={props.componentValue.value ?? ''}
				maxLength={maximumCharacterLimit}
				data-testid={`TextComponent-${props.componentValue.component_id}-selected`}
				scale={props.scale}
			/>
			<Flex
				style={{
					...counterStyle(props.scale, currentCharCount, maximumCharacterLimit),
				}}
			>
				{currentCharCount}/{maximumCharacterLimit}
			</Flex>
		</div>
	) : (
		<StyledInputWrapper
			textAlign={props.textAlign}
			borderColor={borderColor}
			backgroundColor={backgroundColor}
			color={colorPalette.neutral13}
			token={token}
			autoFocus
			onChange={onChange}
			value={value}
			data-testid={`TextComponent-${props.componentValue.component_id}-selected`}
			scale={props.scale}
		/>
	);
};
